import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    AspectRatio,
    Link,
    LinkBox,
    LinkOverlay,
    useBreakpointValue,
    useStyleConfig
} from '@chakra-ui/react'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import {useUrlFromLink} from '../../helpers/urlHelper'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * PDCHomeServicesPushTile component.
 * @typedef {Object} PDCHomeServicesPushTileProps
 * @property {string} title - Title of the tile.
 * @property {string} desc - Description of the tile.
 * @property {string} link - Link of the tile.
 * @property {string} linkLabel - Label of the link.
 * @property {object} smallImage - Small image object.
 * @property {object} mediumImage - Medium image object.
 * @property {object} largeImage - Large image object.
 * @property {object} xlargeImage - Extra large image object.
 * @property {object} xxlargeImage - Extra extra large image object.
 * @property {string} imageTitle - Title of the image.
 * @property {string} imageAlt - Alt of the image.
 * @param {boolean} displayPriority - Should image be loaded with high priority.
 * @property {object} custom - Custom object.
 * @returns {React.ReactElement} - PDCHomeServicesPushTile component.
 */
export const PDCHomeServicesPushTile = ({
    title,
    desc,
    link,
    linkLabel,
    linkInNewTab,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    custom
}) => {
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: smallImage,
            mediumEl: mediumImage,
            largeEl: largeImage,
            xlargeEl: xlargeImage,
            xxlargeEl: xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    const updatedUrl = useUrlFromLink(custom?.link) || link
    const isLinkExternal = custom?.link?.type === 'EXTERNAL'
    const styles = useStyleConfig('Link', {})

    return (
        <LinkBox
            as="article"
            display="flex"
            justifyContent={'center'}
            alignItems={'flex-start'}
            textAlign={'center'}
            textDecoration="none"
            flex="1"
            px={[21, null, null, 55]}
        >
            <Box width={['100%', null, '25vw', '292px']}>
                <AspectRatio ratio={1} height={'100%'}>
                    <PDCImage
                        currentImage={currentImage}
                        imageWidths={DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice}
                        imageAlt={imageAlt}
                        imageTitle={imageTitle}
                        displayPriority={displayPriority}
                        title={title}
                        skeletonProps={{height: '100vh', width: '100%'}}
                    />
                </AspectRatio>
                <Text
                    variant="eyeBrowLarge"
                    mt={20}
                    dangerouslySetInnerHTML={{
                        __html: title
                    }}
                />
                {desc && (
                    <Text
                        mt={10}
                        variant="bodySmall"
                        dangerouslySetInnerHTML={{
                            __html: desc
                        }}
                    />
                )}
                {link && linkLabel && (
                    <LinkOverlay
                        as={isLinkExternal ? Link : RouteLink}
                        href={updatedUrl}
                        to={updatedUrl}
                        {...styles}
                        display={'inline-block'}
                        mt={10}
                        color={'inherit'}
                        target={linkInNewTab ? '_blank' : undefined}
                        rel={linkInNewTab ? 'noopener noreferrer' : undefined}
                    >
                        <Text
                            as="span"
                            display={'block'}
                            variant="bodyBase1"
                            textDecoration="underline"
                            fontWeight="bold"
                        >
                            {linkLabel}
                        </Text>
                    </LinkOverlay>
                )}
            </Box>
        </LinkBox>
    )
}

PDCHomeServicesPushTile.propTypes = {
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
    link: PropTypes.string,
    linkLabel: PropTypes.string,
    linkInNewTab: PropTypes.bool,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    custom: PropTypes.shape({
        link: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        })
    })
}
